import Vue from 'vue'
import Vuex from 'vuex'
import { UserConfigService } from '@/services'
import vuetify from './plugins/vuetify'
import { loadLanguageAsync, BACKEND_TO_LOCALE } from '@/i18n'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    barImage: '',
    drawer: null,
    snackbar: {
      show: false,
      color: 'success',
      text: ''
    },
    user: null,
    userConfig: null, // Configurações do usuário
    // Estado do modo visualização
    viewOnlyMode: {
      active: false,
      managerId: null,
      managerName: null,
      managerRole: null
    }
  },
  mutations: {
    SET_BAR_IMAGE (state, payload) {
      state.barImage = payload
    },
    SET_DRAWER (state, payload) {
      state.drawer = payload
    },
    setSnackbar (state, payload) {
      state.snackbar = {
        show: payload.show !== undefined ? payload.show : true,
        color: payload.color || 'success',
        text: payload.text || ''
      }
    },
    SET_USER (state, user) {
      state.user = user
    },
    CLEAR_USER (state) {
      state.user = null
    },
    SET_USER_CONFIG (state, config) {
      state.userConfig = config
    },
    CLEAR_USER_CONFIG (state) {
      state.userConfig = null
    },
    SET_VIEW_ONLY_MODE (state, payload) {
      state.viewOnlyMode = {
        active: payload.active,
        managerId: payload.managerId,
        managerName: payload.managerName,
        managerRole: payload.managerRole
      }
    },
    CLEAR_VIEW_ONLY_MODE (state) {
      state.viewOnlyMode = {
        active: false,
        managerId: null,
        managerName: null,
        managerRole: null
      }
    }
  },
  actions: {
    showSnackbar ({ commit }, payload) {
      commit('setSnackbar', payload)
    },
    hideSnackbar ({ commit }) {
      commit('setSnackbar', { show: false })
    },
    setUser ({ commit }, user) {
      commit('SET_USER', user)
    },
    clearUser ({ commit }) {
      commit('CLEAR_USER')
    },
    async loadUserConfig ({ commit }) {
      try {
        console.log('🔧 Carregando configurações do usuário...')
        const response = await UserConfigService.getConfig()
        const config = response?.data || UserConfigService.getDefaultConfig()
        
        commit('SET_USER_CONFIG', config)
        console.log('✅ Configurações carregadas:', config)

        // Aplicar idioma salvo
        if (config?.appearanceSettings?.language) {
          const locale = BACKEND_TO_LOCALE[config.appearanceSettings.language] || 'pt-BR'
          await loadLanguageAsync(locale)
        }

        // Aplicar tema imediatamente se houver configuração de aparência
        if (config?.appearanceSettings?.theme) {
          const theme = config.appearanceSettings.theme
          let isDark = false
          
          if (theme === 'AUTO') {
            isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
          } else {
            isDark = theme === 'DARK'
          }
          
          console.log(`🎨 Aplicando tema: ${theme} (isDark: ${isDark})`)
          
          // Aplicar ao Vuetify IMEDIATAMENTE
          vuetify.framework.theme.dark = isDark
          
          // Atualizar localStorage para persistência
          localStorage.setItem('elevment_dark_mode', isDark)
          
          // Atualizar atributo HTML para CSS customizado
          document.documentElement.setAttribute(
            'data-theme',
            isDark ? 'dark' : 'light'
          )
          
          console.log('✅ Tema aplicado com sucesso!')
        }
        
        return config
      } catch (error) {
        console.error('Erro ao carregar configurações do usuário:', error)
        // Em caso de erro, usar valores padrão
        const defaultConfig = UserConfigService.getDefaultConfig()
        commit('SET_USER_CONFIG', defaultConfig)
        return defaultConfig
      }
    },
    async updateUserConfig ({ commit }, config) {
      try {
        console.log('💾 Atualizando configurações do usuário...')
        const response = await UserConfigService.updateConfig(config)
        commit('SET_USER_CONFIG', response.data)
        
        // Aplicar idioma após salvar
        if (response.data?.appearanceSettings?.language) {
          const locale = BACKEND_TO_LOCALE[response.data.appearanceSettings.language] || 'pt-BR'
          await loadLanguageAsync(locale)
        }

        // Aplicar tema imediatamente após salvar
        if (response.data?.appearanceSettings?.theme) {
          const theme = response.data.appearanceSettings.theme
          let isDark = false
          
          if (theme === 'AUTO') {
            isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
          } else {
            isDark = theme === 'DARK'
          }
          
          console.log(`🎨 Aplicando tema após salvar: ${theme} (isDark: ${isDark})`)
          
          // Aplicar ao Vuetify IMEDIATAMENTE
          vuetify.framework.theme.dark = isDark
          
          // Atualizar localStorage
          localStorage.setItem('elevment_dark_mode', isDark)
          
          // Atualizar atributo HTML
          document.documentElement.setAttribute(
            'data-theme',
            isDark ? 'dark' : 'light'
          )
          
          console.log('✅ Tema aplicado após salvar!')
        }
        
        return response
      } catch (error) {
        console.error('Erro ao atualizar configurações do usuário:', error)
        throw error
      }
    },
    clearUserConfig ({ commit }) {
      commit('CLEAR_USER_CONFIG')
    },
    setViewOnlyMode ({ commit }, payload) {
      commit('SET_VIEW_ONLY_MODE', payload)
    },
    clearViewOnlyMode ({ commit }) {
      commit('CLEAR_VIEW_ONLY_MODE')
    }
  },
  getters: {
    currentUser: state => state.user,
    userConfig: state => state.userConfig,
    notificationSettings: state => state.userConfig?.notificationSettings || {},
    privacySettings: state => state.userConfig?.privacySettings || {},
    appearanceSettings: state => state.userConfig?.appearanceSettings || {},
    isViewOnlyMode: state => state.viewOnlyMode.active,
    viewOnlyManagerId: state => state.viewOnlyMode.managerId,
    viewOnlyManagerName: state => state.viewOnlyMode.managerName,
    viewOnlyManagerRole: state => state.viewOnlyMode.managerRole
  }
})
